import React, { useState, useEffect } from 'react';
import predictionAccuracyService from './predictionAccuracyService';
import settlementService from './footballSettlement';
import { TrendingUp, TrendingDown, Activity, Award, AlertCircle, Download } from 'lucide-react';

const PredictionAccuracyDashboard = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [details, setDetails] = useState([]);
  const [topPerformers, setTopPerformers] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [viewMode, setViewMode] = useState('overview'); // overview, details, trend

  useEffect(() => {
    loadData();
  }, [selectedDate, viewMode]);

  const loadData = async () => {
    setLoading(true);
    try {
      const dateStr = formatDate(selectedDate);

      if (viewMode === 'overview' || viewMode === 'details') {
        const result = await predictionAccuracyService.calculateAccuracyStats(dateStr);
        setStats(result.stats);
        setDetails(result.details);

        // Get top performers
        const performers = await predictionAccuracyService.getTopPerformers(dateStr, 2);
        setTopPerformers(performers.slice(0, 10));
      } else if (viewMode === 'trend') {
        // Load 7-day trend
        const endDate = new Date(selectedDate);
        const startDate = new Date(selectedDate);
        startDate.setDate(startDate.getDate() - 6);

        const trend = await predictionAccuracyService.getAccuracyTrend(
          formatDate(startDate),
          formatDate(endDate)
        );
        setTrendData(trend);
      }
    } catch (error) {
      console.error('Error loading accuracy data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handlePullSettlements = async () => {
    try {
      setLoading(true);
      await settlementService.pullTodayET('morning');
      await loadData(); // Reload after pull
    } catch (error) {
      console.error('Error pulling settlements:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportData = () => {
    if (details.length === 0) return;

    const csv = predictionAccuracyService.exportToCSV(details);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `predictions_${formatDate(selectedDate)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 60) return 'text-green-600';
    if (accuracy >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'HIGH': return 'bg-green-100 text-green-800';
      case 'MED': return 'bg-yellow-100 text-yellow-800';
      case 'LOW': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">PickStar Prediction Accuracy</h1>
          <div className="flex gap-2">
            <button
              onClick={handlePullSettlements}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              disabled={loading}
            >
              Pull Settlements
            </button>
            <button
              onClick={exportData}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center gap-2"
              disabled={details.length === 0}
            >
              <Download size={16} />
              Export CSV
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <input
            type="date"
            value={formatDate(selectedDate)}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            className="px-3 py-2 border rounded-md"
          />

          <div className="flex bg-gray-100 rounded-md">
            <button
              onClick={() => setViewMode('overview')}
              className={`px-4 py-2 rounded-l-md ${viewMode === 'overview' ? 'bg-blue-500 text-white' : ''}`}
            >
              Overview
            </button>
            <button
              onClick={() => setViewMode('details')}
              className={`px-4 py-2 ${viewMode === 'details' ? 'bg-blue-500 text-white' : ''}`}
            >
              Details
            </button>
            <button
              onClick={() => setViewMode('trend')}
              className={`px-4 py-2 rounded-r-md ${viewMode === 'trend' ? 'bg-blue-500 text-white' : ''}`}
            >
              7-Day Trend
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <>
          {/* Overview Stats */}
          {viewMode === 'overview' && stats && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Overall Accuracy</p>
                      <p className={`text-3xl font-bold ${getAccuracyColor(stats.accuracy)}`}>
                        {stats.accuracy}%
                      </p>
                    </div>
                    <Activity className="text-gray-400" size={24} />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {stats.correct}/{stats.settled} correct
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Predictions</p>
                      <p className="text-3xl font-bold">{stats.total}</p>
                    </div>
                    <Award className="text-gray-400" size={24} />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {stats.pending} pending
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Star Coins Used</p>
                      <p className="text-3xl font-bold">{stats.starCoinsUsed}</p>
                    </div>
                    <TrendingUp className="text-gray-400" size={24} />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {stats.starCoinsWasted} wasted on incorrect
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Success Rate</p>
                      <p className="text-3xl font-bold">
                        {stats.settled > 0 ? (
                          <>
                            {stats.correct}
                            <span className="text-green-500">✓</span>
                            {' '}
                            {stats.incorrect}
                            <span className="text-red-500">✗</span>
                          </>
                        ) : 'N/A'}
                      </p>
                    </div>
                    {stats.accuracy >= 50 ? (
                      <TrendingUp className="text-green-500" size={24} />
                    ) : (
                      <TrendingDown className="text-red-500" size={24} />
                    )}
                  </div>
                </div>
              </div>

              {/* Confidence Level Breakdown */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-lg font-semibold mb-4">Accuracy by Confidence Level</h2>
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(stats.byConfidence).map(([level, data]) => (
                    <div key={level} className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-2 ${getConfidenceColor(level)}`}>
                        {level}
                      </div>
                      <p className={`text-2xl font-bold ${getAccuracyColor(data.accuracy)}`}>
                        {data.accuracy}%
                      </p>
                      <p className="text-sm text-gray-600">
                        {data.correct}/{data.total} correct
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Performers */}
              {topPerformers.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-lg font-semibold mb-4">Top Performers</h2>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">User ID</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Accuracy</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Correct</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Total</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Star Coins</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {topPerformers.map((user, idx) => (
                          <tr key={user.userId} className="hover:bg-gray-50">
                            <td className="px-4 py-2">
                              {idx === 0 && '🥇'}
                              {idx === 1 && '🥈'}
                              {idx === 2 && '🥉'}
                              {idx > 2 && idx + 1}
                            </td>
                            <td className="px-4 py-2 text-sm font-mono">
                              {user.userId.substring(0, 8)}...
                            </td>
                            <td className={`px-4 py-2 text-center font-bold ${getAccuracyColor(user.accuracy)}`}>
                              {user.accuracy}%
                            </td>
                            <td className="px-4 py-2 text-center">{user.correct}</td>
                            <td className="px-4 py-2 text-center">{user.total}</td>
                            <td className="px-4 py-2 text-center">{user.starCoinsUsed}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Detailed Predictions */}
          {viewMode === 'details' && details.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold mb-4">Prediction Details</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 text-left">Game</th>
                      <th className="px-2 py-2 text-left">Predicted</th>
                      <th className="px-2 py-2 text-center">Confidence</th>
                      <th className="px-2 py-2 text-center">Score</th>
                      <th className="px-2 py-2 text-left">Actual</th>
                      <th className="px-2 py-2 text-center">Result</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {details.map((pred, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-2 py-2">
                          <div className="text-sm">
                            <div className="font-medium">{pred.home || 'Unknown'}</div>
                            <div className="text-gray-500">vs {pred.away || 'Unknown'}</div>
                          </div>
                        </td>
                        <td className="px-2 py-2 font-medium">{pred.winner}</td>
                        <td className="px-2 py-2 text-center">
                          <span className={`inline-block px-2 py-1 rounded-full text-xs ${getConfidenceColor(pred.confidence)}`}>
                            {pred.confidence} {pred.confidenceNumeric}%
                          </span>
                        </td>
                        <td className="px-2 py-2 text-center">
                          {pred.settlement ? (
                            <span className="font-mono">
                              {pred.settlement.homeScore ?? '-'} - {pred.settlement.awayScore ?? '-'}
                            </span>
                          ) : (
                            <span className="text-gray-400">Pending</span>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {pred.settlement?.actualWinner || '-'}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {pred.isCorrect === true && (
                            <span className="text-green-600 text-xl">✓</span>
                          )}
                          {pred.isCorrect === false && (
                            <span className="text-red-600 text-xl">✗</span>
                          )}
                          {pred.isCorrect === null && (
                            <span className="text-gray-400">⏳</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 7-Day Trend */}
          {viewMode === 'trend' && trendData.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold mb-4">7-Day Accuracy Trend</h2>
              <div className="space-y-4">
                {trendData.map((day) => (
                  <div key={day.date} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{day.date}</p>
                      <p className="text-sm text-gray-600">
                        {day.total} predictions ({day.settled} settled)
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${getAccuracyColor(day.accuracy)}`}>
                          {day.accuracy}%
                        </p>
                        <p className="text-sm text-gray-600">
                          {day.correct}/{day.settled}
                        </p>
                      </div>
                      <div className="w-32 bg-gray-200 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full ${
                            day.accuracy >= 60 ? 'bg-green-500' :
                            day.accuracy >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${day.accuracy}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary Stats */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold mb-2">7-Day Summary</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Total Predictions</p>
                    <p className="font-bold">{trendData.reduce((sum, d) => sum + d.total, 0)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Average Accuracy</p>
                    <p className="font-bold">
                      {(
                        trendData.reduce((sum, d) => sum + parseFloat(d.accuracy || 0), 0) /
                        trendData.filter(d => d.accuracy > 0).length
                      ).toFixed(2)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Star Coins Used</p>
                    <p className="font-bold">{trendData.reduce((sum, d) => sum + d.starCoinsUsed, 0)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PredictionAccuracyDashboard;